import * as v from 'valibot';

/**
 * Which model a step runs on, as a resolved triple rather than a string.
 *
 * `StepConfig.model` was a bare `"anthropic/claude-sonnet-5"`, and three things
 * were impossible with it. The rate table could not price a model it did not
 * recognise as anything other than the top Anthropic tier, because a string
 * does not say whose model it is. A person could not say "run my steps on the
 * cheap one" without editing every workflow they can reach. And "a smaller
 * model on the machine with the diff, the larger one on the pull request" —
 * which the README claims reef does — could not express *how hard* either one
 * should think.
 *
 * So a choice is up to three independent answers: who provides it, which model,
 * and how much effort. Each is separately absent, which is what makes the
 * preference layer safe: a document that named a model but not an effort is
 * saying nothing about effort, and only silence can be filled in.
 */

/**
 * How hard the model should work.
 *
 * Three levels, not the seven the runtime's `ThinkingLevel` accepts. A workflow
 * document should express intent — this step is routine, this one is the one
 * that matters — rather than tune a provider's knob, and a document that pinned
 * `xhigh` would be a document that has to be re-reviewed whenever a provider
 * renames its levels. The three names are spelled the way the runtime spells
 * them so the value passes straight through: a translation table between two
 * vocabularies is a table that drifts.
 */
export const ModelEffort = v.picklist(['low', 'medium', 'high']);
export type ModelEffort = v.InferOutput<typeof ModelEffort>;

/**
 * The provider whose model a bare name refers to.
 *
 * Every model id committed to `workflows/` today is written without a provider
 * or with `anthropic/` in front of it, so a bare name has always meant
 * Anthropic and has to keep meaning it — the alternative is a migration of the
 * documents, and a workflow that stops parsing is a workflow that stops running.
 */
export const DEFAULT_MODEL_PROVIDER = 'anthropic';

/** The default when nothing — document, preference or deployment — says. */
export const DEFAULT_MODEL_EFFORT: ModelEffort = 'medium';

/**
 * A model choice with every field resolved. What the runtime is handed.
 */
export interface ModelSelection {
  provider: string;
  model: string;
  effort: ModelEffort;
}

/**
 * A model choice as somebody wrote it — a document, or a person's preference.
 *
 * Every field optional, because partial is the point: `{ effort: 'high' }` is a
 * step saying "whatever model, think hard", and a preference of
 * `{ provider: 'openai', model: 'gpt-5-mini' }` is a person saying nothing
 * about effort.
 */
export interface ModelChoice {
  provider?: string;
  model?: string;
  effort?: ModelEffort;
}

/**
 * What `StepConfig.model` accepts: the string every committed workflow already
 * uses, or the triple.
 */
export type ModelRef = string | ModelChoice;

/*
 * `provider` may not contain a slash — it is the part before the first one in a
 * `provider/model` specifier, so a slash in it would make the specifier
 * ambiguous. `model` may, because some gateways address a model as
 * `openrouter/anthropic/claude-x`, and splitting on the *first* slash keeps that
 * round-tripping.
 */
const ProviderName = v.pipe(
  v.string(),
  v.minLength(1),
  v.regex(/^[^\s/]+$/, 'a provider name has no slashes or spaces'),
);
const ModelName = v.pipe(
  v.string(),
  v.minLength(1),
  v.regex(/^\S+$/, 'a model id has no spaces'),
);

export const ModelChoiceSchema: v.GenericSchema<unknown, ModelChoice> = v.object({
  provider: v.optional(ProviderName),
  model: v.optional(ModelName),
  effort: v.optional(ModelEffort),
});

/**
 * The union a document is parsed against.
 *
 * The string branch is first, and it is the whole reason nothing had to be
 * migrated: `"anthropic/claude-sonnet-5"` in a committed `workflows/*.json`
 * parses exactly as it did, and only becomes a triple when something asks it to.
 */
export const ModelRefSchema: v.GenericSchema<unknown, ModelRef> = v.union([
  v.pipe(v.string(), v.minLength(1)),
  ModelChoiceSchema,
]);

/** Split a `provider/model` specifier. A bare name is the default provider's. */
export function parseModelId(id: string): { provider: string; model: string } {
  const slash = id.indexOf('/');
  if (slash <= 0 || slash === id.length - 1) {
    return { provider: DEFAULT_MODEL_PROVIDER, model: id };
  }
  return { provider: id.slice(0, slash), model: id.slice(slash + 1) };
}

/** The canonical `provider/model` specifier: what the runtime, the ledger and the allowlist all key on. */
export function modelId(selection: Pick<ModelSelection, 'provider' | 'model'>): string {
  return `${selection.provider}/${selection.model}`;
}

/** A ref as its author wrote it, normalized to the pair-and-effort shape. */
function asChoice(ref: ModelRef | null | undefined): ModelChoice {
  if (ref === null || ref === undefined) return {};
  if (typeof ref === 'string') {
    const { provider, model } = parseModelId(ref);
    return { provider, model };
  }
  return ref;
}

export interface ModelResolution {
  /** The step document's own choice, if it made one. */
  document?: ModelRef | null;
  /** The acting person's standing preference, if they have one. */
  preference?: ModelRef | null;
  /** The deployment default — `REEF_DEFAULT_MODEL`. Always present, so a resolution always answers. */
  fallback: string;
}

/**
 * Resolve one step's model: the document wins where it is explicit, the
 * person's preference fills in where it is not.
 *
 * **Provider and model are resolved as a pair, never field by field.** A
 * document that names `claude-haiku-4-5` and a preference that names `openai`
 * must not combine into `openai/claude-haiku-4-5`, which is a model nobody
 * sells and which would fail at the provider with an error naming neither of
 * the two people who caused it. So whoever supplies the model supplies the
 * provider alongside it.
 *
 * Effort *is* resolved on its own, because it is meaningful against any model:
 * a person who has said "I want low effort" means it whichever model a step
 * pins, and a step that says `{ effort: 'high' }` means it is the step that
 * matters rather than that it wants a particular vendor.
 */
export function resolveModel(input: ModelResolution): ModelSelection {
  const document = asChoice(input.document);
  const preference = asChoice(input.preference);
  const fallback = parseModelId(input.fallback);

  const pair = document.model
    ? { provider: document.provider ?? DEFAULT_MODEL_PROVIDER, model: document.model }
    : preference.model
      ? { provider: preference.provider ?? DEFAULT_MODEL_PROVIDER, model: preference.model }
      : fallback;

  return {
    provider: pair.provider,
    model: pair.model,
    effort: document.effort ?? preference.effort ?? DEFAULT_MODEL_EFFORT,
  };
}

/**
 * True when the document pinned a model, so no preference may move it.
 *
 * The allowlist question in `@reef/llm-gateway` is about the *resolved* model,
 * but the widening question is about this: a step limited to one model has to
 * stay on it whoever runs it.
 */
export function modelIsPinned(ref: ModelRef | null | undefined): boolean {
  return asChoice(ref).model !== undefined;
}
